import { useParams } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  Dialog,
  DialogContent,
  Skeleton,
  IconButton,
  Grid,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { Notification } from '@components/prereg_validation/notification'
import { useSessionContext } from '@providers/session'
import { i18WithParams as t } from '@shared/locale'
import { useClientLocationCameras } from 'service-api/src/hooks/use_client_location_cameras'
import { useEffect, useState } from 'react'
import { useLocationContext } from '@providers/location'
import { ClientLocationCamera } from 'service-api/src/types/cameras-api'
import ThumbnailCamera from 'shared/icons/Thumbnail_Camera.svg'

const IDOG_URL = 'https://idogcam.com/idogcamviewer.php?id'

// Camera access should be revoked when a pet is checked out, so re-verify
// periodically rather than trusting the initial load for as long as the page stays open.
const CAMERA_ACCESS_REFETCH_INTERVAL = 10 * 60 * 1000 // 10 minute

export const Cameras = () => {
  const [showVideo, setShowVideo] = useState<boolean>(false)
  const [activeCameraId, setActiveCameraId] = useState<string | null>(null)
  const { location } = useLocationContext()
  const sessionCtx = useSessionContext()
  const { accountName: locationName = '' } = useParams()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))

  const {
    data: cameras,
    isLoading,
    isError,
  } = useClientLocationCameras(locationName, sessionCtx.state?.user?.lupId || '', {
    refetchInterval: CAMERA_ACCESS_REFETCH_INTERVAL,
    refetchIntervalInBackground: true,
  })

  const handleOpenStream = async (camera: ClientLocationCamera) => {
    if (!camera.auth) return

    setActiveCameraId(camera.id ?? null)
    setShowVideo(true)

    setTimeout(() => {
      // Create the form inside the Dialog
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = `${IDOG_URL}=${camera.externalId}`
      form.target = 'video-frame'
      form.style.display = 'none'

      const authInput = document.createElement('input')
      authInput.type = 'hidden'
      authInput.name = 'idogcamauth'
      authInput.value = camera.auth || ''

      form.appendChild(authInput)
      document.body.appendChild(form)

      form.submit()

      // Clean up the form after submission
      document.body.removeChild(form)
    }, 100) // Small delay to allow the iframe to load
  }

  const handleClose = () => {
    setShowVideo(false)
    setActiveCameraId(null)
  }

  // If a stream is open and a revalidation shows the camera is no longer accessible
  // (or no longer authorized), stop the stream instead of leaving it playing.
  useEffect(() => {
    const stillAuthorized = cameras?.some((camera) => camera.id === activeCameraId && camera.auth)
    if (!stillAuthorized) {
      handleClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameras])

  return (
    <Box sx={{ width: '100%' }}>
      <Notification lupId={sessionCtx.state.user?.lupId || ''} />

      <Typography variant="h2" sx={{ mb: 2 }} pl={isBelowMD ? 2 : 0}>
        {t('profile.cameras.title')}
      </Typography>
      <Paper elevation={1} sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ mb: 2 }} pl={isBelowMD ? 2 : 0}>
          {t('profile.cameras.subTitle')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {isLoading && (
            <Grid container spacing={2} data-testid="cameras-loader">
              {Array.from({ length: 4 }).map((_, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 1 }} />
                </Grid>
              ))}
            </Grid>
          )}
          {isError && (
            <Typography mt={2} variant="body2" color="text.secondary">
              {t('profile.cameras.errorMessage')}
            </Typography>
          )}
          {!isLoading && !isError && cameras?.length === 0 && (
            <Typography mt={2} variant="body2" color="text.secondary">
              {t('profile.cameras.emptyMessage', { locationName: location?.displayName || '' })}
            </Typography>
          )}

          {/* Handle Success State */}
          {!isLoading && !isError && (
            <Grid container spacing={2}>
              {cameras?.map((camera: ClientLocationCamera) => (
                <Grid item xs={12} sm={6} key={camera.id}>
                  <Paper elevation={1} sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h4" color="#30281D" gutterBottom>
                      {camera.title}
                    </Typography>

                    {/* Image or placeholder */}
                    <Box
                      sx={{
                        backgroundColor: '#EDE7F6',
                        height: 150,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        overflow: 'hidden',
                      }}
                    >
                      {camera?.isCameraOn ? (
                        <img
                          data-testid="camera-preview"
                          src={camera?.thumbnail}
                          alt={camera.title}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <Box sx={{ textAlign: 'center' }}>
                          <img data-testid="camera-thumbnail" src={ThumbnailCamera} />
                        </Box>
                      )}
                    </Box>

                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ alignSelf: 'flex-end', width: '150px' }}
                      onClick={() => handleOpenStream(camera)}
                    >
                      {t('profile.cameras.button')}
                    </Button>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Paper>
      <Dialog
        maxWidth={isBelowMD ? false : 'md'}
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            height: isBelowMD ? '50%' : 'auto',
          },
        }}
        open={showVideo}
        onClose={handleClose}
      >
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            padding: '0px',
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={(event) => {
              event.stopPropagation()
              handleClose()
            }}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              zIndex: 1,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <iframe
            name="video-frame"
            width="100%"
            height={isBelowMD ? '100%' : '685px'}
            style={{
              border: 'none',
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  )
}
